<template>
    <div class="w-full bg-white p-6 rounded-xl shadow-sm">
        <!-- Header with methodology description -->
        <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-800 mb-2">{{ $t('orgDashboard.reports.nineCellMatrixTitle') }}</h2>
            <p class="text-gray-600">{{ $t('orgDashboard.reports.methodologyDescription') }}</p>
        </div>

        <!-- 9-Cell Matrix with proper methodology -->
        <div v-if="isLoading" class="text-center py-12">
            <div class="text-gray-500">
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('orgDashboard.reports.loading') }}</h3>
                <p class="mt-1 text-sm text-gray-500">{{ $t('orgDashboard.reports.preparingMatrix') }}</p>
            </div>
        </div>

        <div v-else-if="!chartData || chartData.length === 0" class="text-center py-12">
            <div class="text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('orgDashboard.reports.noDataAvailable') }}</h3>
                <p class="mt-1 text-sm text-gray-500">{{ $t('orgDashboard.reports.employeeAssessmentRequired') }}</p>
            </div>
        </div>

        <div v-else class="overflow-hidden rounded-xl border border-gray-100 mb-6">
            <!-- Y-axis label on the left side in vertical text -->
            <div class="flex">
                <div class="hidden sm:flex items-center justify-center w-8 text-gray-600 font-medium"
                    style="writing-mode: vertical-rl; transform: rotate(180deg);">
                    {{ $t('orgDashboard.reports.successionReadiness') }}
                </div>

                <!-- Matrix grid -->
                <div class="flex-1">
                    <!-- Top row (High Succession Readiness) -->
                    <div class="grid grid-cols-3 gap-2 p-2 bg-gray-50">
                        <!-- Cell: High Succession, Low Performance -->
                        <div class="rounded-lg p-4 shadow-sm"
                            style="background-color: #F6BCBB; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                {{ $t('orgDashboard.reports.bargain') }}
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('high', 'low') }} {{ $t('orgDashboard.reports.employees') }}
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('high', 'low')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cell: High Succession, Medium Performance -->
                        <div class="rounded-lg p-4 shadow-sm"
                            style="background-color: #C4D8F3; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                {{ $t('orgDashboard.reports.bind') }}
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('high', 'medium') }} {{ $t('orgDashboard.reports.employees') }}
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('high', 'medium')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cell: High Succession, High Performance -->
                        <div class="rounded-lg p-4 shadow-sm"
                            style="background-color: #C4E888; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                {{ $t('orgDashboard.reports.backup') }}
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('high', 'high') }} {{ $t('orgDashboard.reports.employees') }}
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('high', 'high')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Middle row (Medium Succession Readiness) -->
                    <div class="grid grid-cols-3 gap-2 p-2 bg-gray-50">
                        <!-- Cell: Medium Succession, Low Performance -->
                        <div class="rounded-lg p-4 shadow-sm"
                            style="background-color: #C4D8F3; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                BIND
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('medium', 'low') }} Employees
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('medium', 'low')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cell: Medium Succession, Medium Performance -->
                        <div class="rounded-lg p-4 shadow-sm"
                            style="background-color: #D4EEA5; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                {{ $t('orgDashboard.reports.onTrack') }}
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('medium', 'medium') }} {{ $t('orgDashboard.reports.employees') }}
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('medium', 'medium')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cell: Medium Succession, High Performance -->
                        <div class="rounded-lg p-4 shadow-sm"
                            style="background-color: #EFDBA6; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                {{ $t('orgDashboard.reports.boost') }}
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('medium', 'high') }} {{ $t('orgDashboard.reports.employees') }}
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('medium', 'high')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Bottom row (Low Succession Readiness) -->
                    <div class="grid grid-cols-3 gap-2 p-2 bg-gray-50">
                        <!-- Cell: Low Succession, Low Performance -->
                        <div class="rounded-lg p-4 shadow-sm"
                            style="background-color: #B0EEA5; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                {{ $t('orgDashboard.reports.onTrack') }}
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('low', 'low') }} {{ $t('orgDashboard.reports.employees') }}
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('low', 'low')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cell: Low Succession, Medium Performance -->
                        <div class="rounded-lg p-4 shadow-sm"
                            style="background-color: #EFDBA6; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                {{ $t('orgDashboard.reports.build') }}
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('low', 'medium') }} {{ $t('orgDashboard.reports.employees') }}
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('low', 'medium')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Cell: Low Succession, High Performance -->
                        <div class=" rounded-lg p-4 shadow-sm"
                            style="background-color: #F6BCBB; height: calc(33vh - 80px);">
                            <div class="font-bold text-lg text-black mb-1">
                                {{ $t('orgDashboard.reports.bootBarter') }}
                            </div>
                            <div class="text-sm text-gray-600 mb-3">
                                {{ getCellCount('low', 'high') }} {{ $t('orgDashboard.reports.employees') }}
                            </div>

                            <div class="flex flex-wrap gap-1">
                                <div v-for="item in getCellItems('low', 'high')" :key="item.name"
                                    class="w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                                    :style="getAvatarStyle(item)" @mouseenter="showTooltip(item, $event)"
                                    @mouseleave="hideTooltip">
                                    <span class="text-xs text-white font-bold">{{ getInitials(item.name) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- X-axis label on the bottom -->
            <div class="flex justify-center py-2 border-t border-gray-100">
                <div class="font-medium text-sm text-gray-600">{{ $t('orgDashboard.reports.performanceScore') }}</div>
            </div>
        </div>

        <!-- Tooltip for employee details -->
        <div v-if="tooltipVisible" class="fixed bg-white p-3 rounded-lg border border-gray-200 shadow-lg text-sm z-50"
            :style="{ left: tooltipX + 'px', top: tooltipY + 'px', maxWidth: '250px' }">
            <div v-if="tooltipData">
                <div class="font-bold mb-2 text-gray-800">{{ tooltipData.name }}</div>
                <div v-for="(value, key) in tooltipData" :key="key" class="flex justify-between mb-1"
                    v-if="key !== 'name'">
                    <span class="font-medium text-gray-600 mr-2">{{ key }}:</span>
                    <span class="text-gray-800">{{ typeof value === 'number' ? value.toFixed(1) : value }}</span>
                </div>
            </div>
        </div>

        <!-- Methodology explanation section -->
        <div class="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
            <div class="flex items-start mb-4">
                <div class="flex-shrink-0 mr-4">
                    <div class="w-10 h-10 bg-primary flex items-center justify-center rounded-full text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clip-rule="evenodd" />
                        </svg>
                    </div>
                </div>

                <div class="flex-1">
                    <h3 class="text-lg font-semibold mb-1 text-gray-800">{{ $t('orgDashboard.reports.methodologyTitle') }}</h3>
                    <p class="text-gray-600">{{ $t('orgDashboard.reports.methodologySubtitle') }}</p>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                <div class="bg-white p-5 rounded-lg border border-gray-100 shadow-sm">
                    <h4 class="font-medium text-base text-gray-800 mb-3 flex items-center">
                        <span
                            class="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full mr-2 text-sm">1</span>
                        {{ $t('orgDashboard.reports.calculationMethod') }}
                    </h4>

                    <div class="space-y-3">
                        <div class="bg-blue-50 p-3 rounded-lg">
                            <div class="font-medium text-blue-800 mb-2">{{ $t('orgDashboard.reports.performanceScoreFormula') }}</div>
                            <div class="text-sm text-blue-700">
                                {{ $t('orgDashboard.reports.performanceScoreCalculation') }}
                            </div>
                            <div class="text-xs text-blue-600 mt-1">
                                {{ $t('orgDashboard.reports.performanceScoreNote') }}
                            </div>
                        </div>

                        <div class="bg-green-50 p-3 rounded-lg">
                            <div class="font-medium text-green-800 mb-2">{{ $t('orgDashboard.reports.successionReadinessFormula') }}</div>
                            <div class="text-sm text-green-700">
                                {{ $t('orgDashboard.reports.successionReadinessCalculation') }}
                            </div>
                            <div class="text-xs text-green-600 mt-1">
                                {{ $t('orgDashboard.reports.successionReadinessNote') }}
                            </div>
                        </div>

                        <div class="bg-purple-50 p-3 rounded-lg">
                            <div class="font-medium text-purple-800 mb-2">{{ $t('orgDashboard.reports.yearsScore') }}</div>
                            <div class="text-sm text-purple-700">
                                {{ $t('orgDashboard.reports.yearsScoreCalculation') }}
                            </div>
                            <div class="text-xs text-purple-600 mt-1">
                                {{ $t('orgDashboard.reports.yearsScoreNote') }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-white p-5 rounded-lg border border-gray-100 shadow-sm">
                    <h4 class="font-medium text-base text-gray-800 mb-3 flex items-center">
                        <span
                            class="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full mr-2 text-sm">2</span>
                        {{ $t('orgDashboard.reports.cellCategories') }}
                    </h4>

                    <div class="space-y-2 text-sm">
                        <div class="flex justify-between">
                            <span class="font-medium">{{ $t('orgDashboard.reports.backup') }}:</span>
                            <span class="text-gray-600">{{ $t('orgDashboard.reports.backupDescription') }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="font-medium">{{ $t('orgDashboard.reports.boost') }}:</span>
                            <span class="text-gray-600">{{ $t('orgDashboard.reports.boostDescription') }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="font-medium">{{ $t('orgDashboard.reports.bind') }}:</span>
                            <span class="text-gray-600">{{ $t('orgDashboard.reports.bindDescription') }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="font-medium">{{ $t('orgDashboard.reports.onTrack') }}:</span>
                            <span class="text-gray-600">{{ $t('orgDashboard.reports.onTrackDescription') }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="font-medium">{{ $t('orgDashboard.reports.build') }}:</span>
                            <span class="text-gray-600">{{ $t('orgDashboard.reports.buildDescription') }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="font-medium">{{ $t('orgDashboard.reports.bargain') }}:</span>
                            <span class="text-gray-600">{{ $t('orgDashboard.reports.bargainDescription') }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="font-medium">{{ $t('orgDashboard.reports.bootBarter') }}:</span>
                            <span class="text-gray-600">{{ $t('orgDashboard.reports.bootBarterDescription') }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import constants from "@/constants";
import { kvdrOrgConfig } from "@/assets/data/kvdrOrgConfigs.js";

export default {
    name: 'NineCellMatrix',
    data() {
        return {
            tooltipVisible: false,
            tooltipX: 0,
            tooltipY: 0,
            tooltipData: null,
            kvdrConfig: {},
            isLoading: true,
            // Colors for avatars - matching your UI
            avatarColors: [
                '#FF5733', // Red-Orange
                '#33A8FF', // Blue
                '#33FF57', // Green
                '#FF33A8', // Pink
                '#A833FF', // Purple
                '#FFBD33', // Orange
                '#33FFF6', // Cyan
                '#FF3333', // Red
                '#33FF33', // Bright Green
                '#3333FF', // Deep Blue
                '#FFFF33', // Yellow
                '#FF33FF'  // Magenta
            ]
        };
    },
    computed: {
        userId() {
            return this.$store.state.auth.user._id;
        },
        orgData() {
            return this.$store.state.organization.orgData;
        },
        kvdrData() {
            return this.$store.state.kvdr.selectedKVDR;
        },
        employeesData() {
            return this.$store.state.organization.employees;
        },
        chartData() {
            if (!this.kvdrData || !this.kvdrConfig) {
                return [];
            }

            return this.extractMatrixData();
        }
    },
    watch: {
        async kvdrData(val) {
            if (val) {
                this.kvdrConfig = kvdrOrgConfig(this.orgData._id);
            }
        }
    },
    methods: {
        getUserById(userId) {
            return this.employeesData.filter((user) => user.userId === userId);
        },
        extractMatrixData() {
            if (!this.kvdrData || !this.kvdrConfig) {
                console.warn('Missing KVDR data or config');
                return [];
            }

            if (!this.kvdrData.kvdrRoles || this.kvdrData.kvdrRoles.length === 0) {
                console.warn('No KVDR roles found');
                return [];
            }

            const result = [];

            for (const index in this.kvdrData.kvdrRoles) {
                const kvdrRoles = this.kvdrData.kvdrRoles[index];

                if (!kvdrRoles.kvdrRoleProfiles || kvdrRoles.kvdrRoleProfiles.length === 0) {
                    continue; // Skip roles without profiles
                }

                kvdrRoles.kvdrRoleProfiles.forEach((e) => {
                    const filteredUser = this.getUserById(e.userId);
                    if (filteredUser && filteredUser.length > 0) {
                        const tmpUser = filteredUser[0];

                        // Calculate Performance Score (P) = 0.6 × Performance + 0.4 × Appraisal
                        const performanceScore = this.calculatePerformanceScore(e);

                        // Calculate Succession Readiness (R) = 0.5 × Succession + 0.3 × Fitment + 0.2 × Years Score
                        const successionReadiness = this.calculateSuccessionReadiness(e);

                        if (performanceScore !== null && successionReadiness !== null) {
                            // Log calculation details for debugging
                            this.logCalculationDetails(tmpUser.fullName, e, performanceScore, successionReadiness);

                            result.push({
                                name: tmpUser.fullName,
                                role: kvdrRoles.title,
                                avatar: tmpUser.userData.profilePictureUrl
                                    ? constants.s3Bucket + '/editor/profile-pictures/' + tmpUser.userData.profilePictureUrl
                                    : 'https://hostedboringavatars.vercel.app/api/beam?name=' + tmpUser.userData.firstName,
                                performanceScore: performanceScore,
                                successionReadiness: successionReadiness,
                                // Store raw scores for tooltip
                                rawScores: {
                                    performance: this.extractUserScore(e.performanceData),
                                    appraisal: this.extractUserScore(e.appraisalData),
                                    succession: this.extractUserScore(e.successionData),
                                    fitment: this.extractUserScore(e.fitmentData),
                                    yearsInRole: this.extractYearsInRole(e)
                                }
                            });
                        } else {
                            console.warn('Skipping user ' + tmpUser.fullName + ' due to missing or invalid assessment data');
                        }
                    } else {
                        console.warn('User not found for userId: ' + e.userId);
                    }
                });
            }

            console.log('Processed ' + result.length + ' employees for Nine-Cell Matrix');
            return result.sort((a, b) => a.name.localeCompare(b.name));
        },

        // Calculate Performance Score: P = 0.6 × Performance + 0.4 × Appraisal
        calculatePerformanceScore(profile) {
            const performance = this.extractUserScore(profile.performanceData);
            const appraisal = this.extractUserScore(profile.appraisalData);

            if (performance === null || appraisal === null) {
                console.warn('Missing performance or appraisal data for profile:', profile);
                return null;
            }

            // Convert from -1 to 1.5 range to 0-100 scale
            // -1 = 0, 0 = 50, 1.5 = 100
            const normalizedPerf = this.normalizeScore(performance);
            const normalizedApp = this.normalizeScore(appraisal);

            // Apply weights: P = 0.6 × Performance + 0.4 × Appraisal
            const perfScore = normalizedPerf * 0.6;
            const appScore = normalizedApp * 0.4;

            return Math.round(perfScore + appScore);
        },

        // Calculate Succession Readiness: R = 0.5 × Succession + 0.3 × Fitment + 0.2 × Years Score
        calculateSuccessionReadiness(profile) {
            const succession = this.extractUserScore(profile.successionData);
            const fitment = this.extractUserScore(profile.fitmentData);
            const yearsInRole = this.extractYearsInRole(profile);

            if (succession === null || fitment === null || yearsInRole === null) {
                console.warn('Missing succession, fitment, or years data for profile:', profile);
                return null;
            }

            // Convert from -1 to 1.5 range to 0-100 scale
            const normalizedSucc = this.normalizeScore(succession);
            const normalizedFit = this.normalizeScore(fitment);
            const yearsScore = this.calculateYearsScore(yearsInRole);

            // Apply weights: R = 0.5 × Succession + 0.3 × Fitment + 0.2 × Years Score
            const succScore = normalizedSucc * 0.5;
            const fitScore = normalizedFit * 0.3;
            const yearsWeightedScore = yearsScore * 0.2;

            return Math.round(succScore + fitScore + yearsWeightedScore);
        },

        // Normalize score from -1 to 1.5 range to 0-100 scale
        // -1 = 0, 0 = 50, 1.5 = 100
        normalizeScore(score) {
            // Map -1 to 0, 0 to 50, 1.5 to 100
            // Formula: ((score + 1) / 2.5) * 100
            return Math.max(0, Math.min(100, ((score + 1) / 2.5) * 100));
        },

        // Calculate Years Score: If Years ≥ 3: 100, else: (Years / 3) × 100
        calculateYearsScore(yearsInRole) {
            if (yearsInRole >= 3) {
                return 100;
            } else {
                return (yearsInRole / 3) * 100;
            }
        },

        // Log calculation details for debugging
        logCalculationDetails(employeeName, profile, performanceScore, successionReadiness) {
            const performance = this.extractUserScore(profile.performanceData);
            const appraisal = this.extractUserScore(profile.appraisalData);
            const succession = this.extractUserScore(profile.successionData);
            const fitment = this.extractUserScore(profile.fitmentData);
            const yearsInRole = this.extractYearsInRole(profile);

            console.log('\n=== Calculation Details for ' + employeeName + ' ===');
            console.log('Raw Scores:');
            console.log('  Performance: ' + (performance ? performance.toFixed(3) : 'N/A') + ' (avg of ' + (profile.performanceData ? profile.performanceData.length : 0) + ' items)');
            console.log('  Appraisal: ' + (appraisal ? appraisal.toFixed(3) : 'N/A') + ' (avg of ' + (profile.appraisalData ? profile.appraisalData.length : 0) + ' items)');
            console.log('  Succession: ' + (succession ? succession.toFixed(3) : 'N/A') + ' (avg of ' + (profile.successionData ? profile.successionData.length : 0) + ' items)');
            console.log('  Fitment: ' + (fitment ? fitment.toFixed(3) : 'N/A') + ' (avg of ' + (profile.fitmentData ? profile.fitmentData.length : 0) + ' items)');
            console.log('  Years in Role: ' + (yearsInRole ? yearsInRole.toFixed(1) : 'N/A'));

            console.log('Normalized Scores (0-100):');
            console.log('  Performance: ' + (performance ? this.normalizeScore(performance).toFixed(1) : 'N/A'));
            console.log('  Appraisal: ' + (appraisal ? this.normalizeScore(appraisal).toFixed(1) : 'N/A'));
            console.log('  Succession: ' + (succession ? this.normalizeScore(succession).toFixed(1) : 'N/A'));
            console.log('  Fitment: ' + (fitment ? this.normalizeScore(fitment).toFixed(1) : 'N/A'));
            console.log('  Years Score: ' + (yearsInRole ? this.calculateYearsScore(yearsInRole).toFixed(1) : 'N/A'));

            console.log('Final Scores:');
            console.log('  Performance Score (P): ' + performanceScore + ' (0.6×' + (performance ? this.normalizeScore(performance).toFixed(1) : 'N/A') + ' + 0.4×' + (appraisal ? this.normalizeScore(appraisal).toFixed(1) : 'N/A') + ')');
            console.log('  Succession Readiness (R): ' + successionReadiness + ' (0.5×' + (succession ? this.normalizeScore(succession).toFixed(1) : 'N/A') + ' + 0.3×' + (fitment ? this.normalizeScore(fitment).toFixed(1) : 'N/A') + ' + 0.2×' + (yearsInRole ? this.calculateYearsScore(yearsInRole).toFixed(1) : 'N/A') + ')');
            console.log('=====================================\n');
        },

        // Extract years in role from profile data
        extractYearsInRole(profile) {
            // Since years in role data is not directly available in the current structure,
            // we'll use a default value or try to extract from other available data
            // This can be enhanced later when years in role data becomes available

            // For now, using a reasonable default that allows the methodology to work
            // In a real implementation, this would come from:
            // - profile.yearsInRole
            // - profile.startDate (calculated from current date)
            // - profile.roleStartDate
            // - or other relevant fields

            return 2.0; // Default to 2 years - this should be replaced with actual data

            // Alternative implementation when data becomes available:
            // if (profile.yearsInRole !== undefined) {
            //     return parseFloat(profile.yearsInRole) || 0;
            // }
            // if (profile.startDate) {
            //     const startDate = new Date(profile.startDate);
            //     const currentDate = new Date();
            //     const diffTime = Math.abs(currentDate - startDate);
            //     const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);
            //     return Math.max(0, diffYears);
            // }
            // return 0;
        },

        extractUserScore(data) {
            if (!data || !Array.isArray(data)) {
                return null;
            }

            // Calculate the average score from all items
            const total = data.reduce((sum, item) => sum + parseFloat(item.value), 0);
            const count = data.length;

            // Return the average score (this will be in the -1 to 1.5 range)
            return count > 0 ? total / count : 0;
        },

        // Get items for a specific cell based on Performance Score and Succession Readiness
        getCellItems(yLevel, xLevel) {
            if (!this.chartData || this.chartData.length === 0) {
                return [];
            }

            return this.chartData.filter(item => {
                const performanceLevel = this.getScoreLevel(item.performanceScore);
                const successionLevel = this.getScoreLevel(item.successionReadiness);

                return performanceLevel === xLevel && successionLevel === yLevel;
            });
        },

        // Get count of items in a cell
        getCellCount(yLevel, xLevel) {
            return this.getCellItems(yLevel, xLevel).length;
        },

        // Determine score level: High (≥75), Medium (50-74), Low (<50)
        getScoreLevel(score) {
            if (score >= 75) return 'high';
            if (score >= 50) return 'medium';
            return 'low';
        },

        // Get employee initials for avatars
        getInitials(name) {
            if (!name) return '';
            return name.split(' ')
                .map(word => word.charAt(0))
                .join('')
                .substring(0, 2)
                .toUpperCase();
        },

        // Get avatar style with consistent color based on employee name
        getAvatarStyle(item) {
            const nameHash = this.hashString(item.name);
            const colorIndex = nameHash % this.avatarColors.length;
            return {
                backgroundColor: this.avatarColors[colorIndex],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            };
        },

        // Simple hash function for consistent color assignment
        hashString(str) {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                hash |= 0; // Convert to 32bit integer
            }
            return Math.abs(hash);
        },

        // Show tooltip with employee details
        showTooltip(item, event) {
            this.tooltipData = {
                name: item.name,
                role: item.role,
                'Performance Score': item.performanceScore,
                'Succession Readiness': item.successionReadiness,
                'Performance (Raw)': item.rawScores && item.rawScores.performance ? item.rawScores.performance.toFixed(2) : 'N/A',
                'Performance (Norm)': item.rawScores && item.rawScores.performance ? this.normalizeScore(item.rawScores.performance).toFixed(1) : 'N/A',
                'Appraisal (Raw)': item.rawScores && item.rawScores.appraisal ? item.rawScores.appraisal.toFixed(2) : 'N/A',
                'Appraisal (Norm)': item.rawScores && item.rawScores.appraisal ? this.normalizeScore(item.rawScores.appraisal).toFixed(1) : 'N/A',
                'Succession (Raw)': item.rawScores && item.rawScores.succession ? item.rawScores.succession.toFixed(2) : 'N/A',
                'Succession (Norm)': item.rawScores && item.rawScores.succession ? this.normalizeScore(item.rawScores.succession).toFixed(1) : 'N/A',
                'Fitment (Raw)': item.rawScores && item.rawScores.fitment ? item.rawScores.fitment.toFixed(2) : 'N/A',
                'Fitment (Norm)': item.rawScores && item.rawScores.fitment ? this.normalizeScore(item.rawScores.fitment).toFixed(1) : 'N/A',
                'Years in Role': item.rawScores && item.rawScores.yearsInRole ? item.rawScores.yearsInRole.toFixed(1) : 'N/A'
            };

            this.tooltipX = event.clientX + 10;
            this.tooltipY = event.clientY - 10;
            this.tooltipVisible = true;
        },

        // Hide tooltip
        hideTooltip() {
            this.tooltipVisible = false;
        },

        // Get total employee count
        getTotalEmployeeCount() {
            return this.chartData.length;
        },

        // Get count of employees with Performance Score ≥ 75
        getHighPerformersCount() {
            return this.chartData.filter(item => item.performanceScore >= 75).length;
        },

        // Get count of employees with Succession Readiness ≥ 75
        getHighSuccessionCount() {
            return this.chartData.filter(item => item.successionReadiness >= 75).length;
        },

        // Get average Performance Score
        getAveragePerformanceScore() {
            if (!this.chartData || this.chartData.length === 0) return 0;
            const total = this.chartData.reduce((sum, item) => sum + item.performanceScore, 0);
            return Math.round(total / this.chartData.length);
        },

        // Get average Succession Readiness Score
        getAverageSuccessionScore() {
            if (!this.chartData || this.chartData.length === 0) return 0;
            const total = this.chartData.reduce((sum, item) => sum + item.successionReadiness, 0);
            return Math.round(total / this.chartData.length);
        }
    },
    mounted() {
        if (this.kvdrData) {
            this.kvdrConfig = kvdrOrgConfig(this.orgData._id);
        }
        this.isLoading = false;
    }
};
</script>